import React, { useContext, useRef } from "react";
import { ReviewsContext } from "../context/ReviewsContext";
import { Card, CardHeader, CardContent, List, ListItem, ListItemText, Box, Typography, Divider } from "@mui/material";
import { DashboardCardTitle, MODERN_CARD_STYLE, CARD_HEADER_STYLE, CARD_CONTENT_STYLE } from './common/ChartDefaults';
import { formatCurrency } from '../utils/currencyUtils';
import { applySalesMultiplier } from '../utils/chartUtils';
import { BusinessContext } from "../context/BusinessContext";
import { colors } from '../utils/themeUtils';
import ChatIcon from '@mui/icons-material/Chat';
import { NoMaxWidthTooltip, TooltipContent, CommentText } from './common/SharedTooltip';
import { styled } from '@mui/material/styles';

const QuoteMark = styled('span')({
  color: 'rgba(0, 0, 0, 0.4)',
  fontWeight: 400,
});

const capitalizeFirstLetter = (string) => {
  if (!string) return "N/A";
  return string.charAt(0).toUpperCase() + string.slice(1);
};

// Format impact values with proper precision for Rating view
const formatImpactExact = (impact) => {
  if (impact === undefined || impact === null) return '0.000 ★';
  
  const absImpact = Math.abs(impact);
  
  // For zero values
  if (absImpact === 0) return '0.000 ★';
  
  // For normal values (≥ 0.001), use standard 3 decimal places
  if (absImpact >= 0.001) {
    return `${absImpact.toFixed(3)} ★`;
  }
  
  // For very small values, find the first significant digit
  const scientificNotation = absImpact.toExponential();
  const matches = scientificNotation.match(/^(\d)\.?(\d*)?e-(\d+)$/);
  
  if (matches) {
    const firstDigit = matches[1]; // First significant digit
    const exponent = parseInt(matches[3], 10);
    
    // Create the string with leading zeros
    let result = '0.';
    for (let i = 1; i < exponent; i++) {
      result += '0';
    }
    result += firstDigit;
    
    return `${result} ★`;
  }
  
  // Fallback to standard formatting
  return `${absImpact.toFixed(3)} ★`;
};

const CommentsSideBySide = () => {
  const scrollContainerRef = useRef(null);
  const {
    reviewsData,
    selectedCategory,
    selectedSubject,
    selectedAttribute,
    selectedDate,
    view,
  } = useContext(ReviewsContext);

  const { selectedBusiness, currencySymbol } = useContext(BusinessContext);

  const positiveComments = [];
  const negativeComments = [];

  (reviewsData || []).forEach((review) => {
    const reviewPositiveComments = [];
    const reviewNegativeComments = [];

    if (selectedDate) {
      const reviewDate = new Date(review.estimated_datetime);
      if (
        reviewDate.getFullYear() !== selectedDate.getFullYear() ||
        reviewDate.getMonth() !== selectedDate.getMonth()
      ) {
        return;
      }
    }

    (review.analysis || []).forEach((item) => {
      if (
        (selectedCategory && item.category?.toLowerCase() !== selectedCategory.toLowerCase()) ||
        (selectedSubject && item.subject_group?.toLowerCase() !== selectedSubject.toLowerCase()) ||
        (selectedAttribute && item.attribute?.toLowerCase() !== selectedAttribute.toLowerCase())
      ) {
        return;
      }

      const commentObj = {
        comment: item.comment,
        category: item.category,
        subject_group: item.subject_group,
        attribute: item.attribute,
        attribute_impact: item.attribute_impact,
        sentiment: item.sentiment,
        reviewId: review.id,
      };

      if (item.sentiment === "positive") {
        reviewPositiveComments.push(commentObj);
      } else if (item.sentiment === "negative") {
        reviewNegativeComments.push(commentObj);
      }
    });

    // Group comments only within this specific review
    const groupCommentsWithinReview = (comments) => {
      const grouped = {};
      comments.forEach((obj) => {
        const key = `${obj.comment}-${obj.reviewId}`; // Include reviewId in the key
        if (!grouped[key]) {
          grouped[key] = [];
        }
        grouped[key].push(obj);
      });
      return Object.values(grouped).map(items => ({
        comment: items[0].comment,
        items,
        reviewId: items[0].reviewId
      }));
    };

    // Add grouped comments for this review to the main arrays
    positiveComments.push(...groupCommentsWithinReview(reviewPositiveComments));
    negativeComments.push(...groupCommentsWithinReview(reviewNegativeComments));
  });

  let title = "Customer Insights";

  if (selectedCategory || selectedSubject || selectedAttribute) {
    title += " for";
    if (selectedCategory) title += ` ${selectedCategory}`;
    if (selectedSubject) title += `${selectedCategory ? "," : ""} ${selectedSubject}`;
    if (selectedAttribute) title += `${(selectedCategory || selectedSubject) ? "," : ""} ${selectedAttribute}`;
  }

  if (selectedDate) {
    const options = { month: 'long', year: 'numeric' };
    const formattedDate = selectedDate.toLocaleDateString(undefined, options);
    title = (
      <>
        {title} <span style={{ color: '#78909C', fontSize: '0.8em', fontWeight: 400 }}> ({formattedDate})</span>
      </>
    );
  }

  // Format impact values with both revenue and precision handling
  const createFormatImpactValueFunction = () => {
    return (cObj) => {
      // Handle zero values with no sign
      if (cObj.attribute_impact === 0) {
        return view === "Sales" ? formatCurrency(0, currencySymbol, view) : '0.000 ★'; 
      }
      
      // For Sales view, calculate revenue impact using turnover
      if (view === "Sales") {
        const numericValue = applySalesMultiplier(
          [cObj.attribute_impact],
          view,
          selectedBusiness?.turnover
        )[0];
        const formattedValue = formatCurrency(numericValue, currencySymbol, view) || 'N/A';
        if (formattedValue === 'N/A') return 'N/A';
        const prefixedValue = numericValue > 0 ? `+${formattedValue}` : formattedValue;
        return prefixedValue;
      } else {
        // For Rating view, use our precision formatter
        const value = cObj.attribute_impact;
        const isPositive = value > 0;
        return `${isPositive ? '+' : '-'}${formatImpactExact(Math.abs(value))}`;
      }
    };
  };

  // Create the formatter function once
  const formatImpactValue = createFormatImpactValueFunction();

  return (
    <Card sx={MODERN_CARD_STYLE}>
      <Box sx={{ height: '56px' }}>
        <CardHeader 
          sx={{
            ...CARD_HEADER_STYLE,
            justifyContent: 'flex-start',
            '& .MuiCardHeader-content': {
              overflow: 'hidden'
            }
          }}
          title={<DashboardCardTitle icon={<ChatIcon />}>{title}</DashboardCardTitle>}
        />
      </Box>
      
      {/* Headers section without divider */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          paddingX: 2,
          pt: 0,
          mb: 0.5,
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              width: '90%',
              textAlign: "center",
              color: '#424242',
              fontWeight: 600,
              fontSize: { xs: "0.8rem", sm: "0.875rem", lg: "0.95rem" },
              borderBottom: `1px solid ${colors.positiveGreen}50`,
              paddingBottom: '4px',
              marginBottom: '4px',
            }}
          >
            Positive
          </Typography>
        </Box>
        
        {/* Removed divider from header section */}
        
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              width: '90%',
              textAlign: "center",
              color: '#424242',
              fontWeight: 600,
              fontSize: { xs: "0.8rem", sm: "0.875rem", lg: "0.95rem" },
              borderBottom: `1px solid ${colors.negativeRed}50`,
              paddingBottom: '4px',
              marginBottom: '4px',
            }}
          >
            Negative
          </Typography>
        </Box>
      </Box>

      {/* Scrollable content section */}
      <CardContent
        ref={scrollContainerRef}
        sx={{
          overflowY: "auto",
          flexGrow: 1,
          padding: { xs: 0, md: 0, lg: 0 },
          paddingTop: 0,
          margin: 0,
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
          }
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            px: 2,
            position: "relative", // Added for absolute positioning of divider
          }}
        >
          {/* Single centered divider for the entire content */}
          <Divider 
            orientation="vertical" 
            sx={{ 
              position: "absolute",
              height: "100%",
              left: "50%",
              transform: "translateX(-50%)",
              opacity: 0.1,
              zIndex: 1
            }} 
          />
          
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              pl: 1,
              pr: 1,
            }}
          >
            <Box
              sx={{
                width: '90%',
              }}
            >
              <List sx={{ py: 0, width: '100%', px: 0 }}>
                {positiveComments.map((commentGroup, index) => (
                  <ListItem 
                    key={`${commentGroup.reviewId}-${index}`} 
                    sx={{ 
                      paddingTop: { xs: 0.25, sm: 0.5, lg: 0.5 }, 
                      paddingBottom: { xs: 0.25, sm: 0.5, lg: 0.5 }, 
                      display: 'block', 
                      px: 0,
                    }}
                  >
                    <Box sx={{ display: 'block', width: '100%', overflow: 'hidden' }}>
                      <NoMaxWidthTooltip
                        title={
                          <TooltipContent 
                            items={commentGroup.items} 
                            formatImpactValue={formatImpactValue}
                            options={{ view, currencySymbol }}
                          />
                        }
                        arrow
                        placement="top"
                      >
                        <Typography
                          variant="body2"
                          sx={(theme) => ({
                            [theme.breakpoints.down('sm')]: { fontSize: '0.75rem' },
                            [theme.breakpoints.down('lg')]: { fontSize: '0.8rem' },
                            display: 'block',
                            color: '#1A2027',
                            width: '100%',
                            wordBreak: 'break-word',
                            whiteSpace: 'normal',
                            textAlign: 'left',
                          })}
                        >
                          <QuoteMark>"</QuoteMark>
                          <CommentText sentiment="positive">
                            {commentGroup.comment}
                          </CommentText>
                          <QuoteMark>"</QuoteMark>
                        </Typography>
                      </NoMaxWidthTooltip>
                    </Box>
                  </ListItem>
                ))}
              </List>
            </Box>
          </Box>
          
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              pl: 1,
              pr: 1,
            }}
          >
            <Box
              sx={{
                width: '90%',
              }}
            >
              <List sx={{ py: 0, width: '100%', px: 0 }}>
                {negativeComments.map((commentGroup, index) => (
                  <ListItem 
                    key={`${commentGroup.reviewId}-${index}`} 
                    sx={{ 
                      paddingTop: { xs: 0.25, sm: 0.5, lg: 0.5 }, 
                      paddingBottom: { xs: 0.25, sm: 0.5, lg: 0.5 }, 
                      display: 'block', 
                      px: 0,
                    }}
                  >
                    <Box sx={{ display: 'block', width: '100%', overflow: 'hidden' }}>
                      <NoMaxWidthTooltip
                        title={
                          <TooltipContent 
                            items={commentGroup.items} 
                            formatImpactValue={formatImpactValue}
                            options={{ view, currencySymbol }}
                          />
                        }
                        arrow
                        placement="top"
                      >
                        <Typography
                          variant="body2"
                          sx={(theme) => ({
                            [theme.breakpoints.down('sm')]: { fontSize: '0.75rem' },
                            [theme.breakpoints.down('lg')]: { fontSize: '0.8rem' },
                            display: 'block',
                            color: '#1A2027',
                            width: '100%',
                            wordBreak: 'break-word',
                            whiteSpace: 'normal',
                            textAlign: 'left',
                          })}
                        >
                          <QuoteMark>"</QuoteMark>
                          <CommentText sentiment="negative">
                            {commentGroup.comment}
                          </CommentText>
                          <QuoteMark>"</QuoteMark>
                        </Typography>
                      </NoMaxWidthTooltip>
                    </Box>
                  </ListItem>
                ))}
              </List>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CommentsSideBySide;
